import log from "loglevel"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { API, NexusSuccessResponse } from "../api/API"
import { PermissionsType, ToolbarOptionType, ToolbarOptions } from "../store/types/AuthTypes"
import { UserGroupType, UserType } from "../store/types/UserTypes"

interface AuthStoreState {
    user?: UserType
    permissions?: PermissionsType
    setNexusClient: (nexusClient: string) => void
    nexusClient?: string
    insiteToken?: string
    setInsiteToken: (token: string) => void
    logout: () => void
    login: () => Promise<void>
    userLoading: boolean
    loginError?: string
    checkTokenExpiration: () => Promise<boolean>

    /**
     * Check if user has access to toolbar sub-option(s).
     *
     * Can take a single name or multiple in an array. Names must exist
     * in ToolbarOptions.
     */
    checkToolbarAccess: (names: ToolbarOptions[] | ToolbarOptions) => boolean

    /**
     * Check if user has access to a NV5G client and is assigned to
     * a Nexus-enabled user group in that client.
     */
    checkNV5GClientAccess: () => boolean
}

export const useAuthStore = create<AuthStoreState>()(
    persist(
        (set, get) => ({
            userLoading: false,
            setNexusClient: (nexusClient: string) => set({ nexusClient }),
            setInsiteToken: (insiteToken?: string) => set({ insiteToken }),
            logout: () => {
                set({
                    insiteToken: undefined,
                    nexusClient: undefined,
                    user: undefined,
                    permissions: undefined,
                    loginError: undefined,
                })
            },
            login: async () => {
                set({ userLoading: true })

                try {
                    const userResponse: NexusSuccessResponse<UserType> = await API.get(
                        "/user/current",
                        "There was an unexpected error logging in. Please contact Nexus support, or refresh the page and try again.",
                    )
                    const user = userResponse.data

                    if (user?.user_groups) {
                        // save client_id with nexus enabled and in nv5gClients
                        const grp = user.user_groups.find(getNv5ClientGroup)
                        if (!grp) {
                            set({
                                userLoading: false,
                                loginError:
                                    "Login was successful, but your user must be assigned a Nexus-enabled user group. \
                        Please contact Nexus support.",
                            })
                            return
                        }

                        // Success path!
                        log.info(`Refreshed auth info for ${user.email}`)
                        set({
                            nexusClient: grp.client_id,
                            user,
                            userLoading: false,
                            permissions: generatePermissions(user.user_groups),
                        })

                        return
                    }
                    set({
                        userLoading: false,
                        loginError:
                            "Login was successful, but the system was unable to fetch your user data. \
                Please contact Nexus support, or refresh the page and try again.",
                    })
                    return
                } catch (error) {
                    log.error(error)

                    set({
                        userLoading: false,
                        loginError: error.message,
                    })
                    return
                }
            },
            checkTokenExpiration: async (): Promise<boolean> => {
                const state = get()
                if (state.insiteToken != null) {
                    const token: any = decodeToken(state.insiteToken)
                    const expire = JSON.parse(token).exp
                    const timeNow = new Date().getTime()

                    if (timeNow > expire * 1000) {
                        log.info("Token expired, so logout.")
                        state.logout()
                        return false
                    }
                    return true
                }
                // No token, of course it's expired
                return false
            },
            checkNV5GClientAccess: () => {
                const userGroups = get()?.user?.user_groups
                if (userGroups == null) {
                    return false
                }
                return userGroups.some(getNv5ClientGroup)
            },
            checkToolbarAccess: (names: ToolbarOptions[] | ToolbarOptions) => {
                const state = get()
                const namesArray = typeof names === "string" ? [names] : names
                return namesArray.some((name) => state.permissions?.[name])
            },
        }),
        {
            name: "AuthStore",
            // Persist to local storage only the nexus client and insite token
            partialize: (state) => ({ nexusClient: state.nexusClient, insiteToken: state.insiteToken }),
        },
    ),
)

// Various private-to-auth helper functions

const nv5gClients = ["qsi-test", "nv5g"]
const toolbarNames: string[] = Object.values(ToolbarOptions)

/**
 * Loops through Nexus suboptions of each user group and record permissions.
 *
 * Permission granted if at least one usergroup has the suboption enabled.
 * Name must be included in ToolbarOptions
 */
const generatePermissions = (userGroups: UserGroupType[]) => {
    const permissions: { [key: string]: boolean } = {}

    userGroups.forEach((userGroup) => {
        const nexusOption = findToolbarOption(userGroup.toolbar_options, "Nexus")

        if (nexusOption?.enabled) {
            nexusOption?.suboptions.forEach((suboption) => {
                const { name, enabled } = suboption
                if (enabled && toolbarNames.includes(name)) {
                    permissions[name] = true
                }
            })
        }
    })

    return permissions
}

/**
 * Finds single toolbar option by name.
 */
const findToolbarOption = (options: ToolbarOptionType[], name: string) => {
    return options.find((opt) => opt.name === name)
}

const decodeToken = (token: string): string => {
    if (token) {
        const base64Url = token.split(".")[1]
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")

        return decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(""),
        )
    }
    return ""
}

/**
 * Returns User group with correct NV5G access and has toolbar option enabled
 *
 * @param userGroup    singular user group option in user object
 */
const getNv5ClientGroup = (userGroup: UserGroupType) => {
    const nexusToolbarEnabled = userGroup.toolbar_options.some((opt) => opt.enabled && opt.name === "Nexus")

    return nv5gClients.includes(userGroup.client_id) && nexusToolbarEnabled
}
